import { useState } from "react";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";

const C = OPS.wm.admin;

/** «إدارة الوحدات» — spec level 4. A unit is never deleted (records point at
 *  its key); it is deactivated, which hides it from new entries only. */
export function UnitsAdmin({ units, onChanged }) {
    const { t, lang } = useI18n();
    const [draft, setDraft] = useState({ key: "", name_ar: "", name_en: "", allows_decimal: false });
    const [busy, setBusy] = useState(false);

    const call = async (fn) => {
        setBusy(true);
        try {
            await fn();
            await onChanged();
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    const add = () => {
        if (!draft.key.trim() || !draft.name_ar.trim()) {
            toast.error(t("wm_admin_unit_required"));
            return;
        }
        call(async () => {
            await api.post("/waste-meals/units", draft);
            setDraft({ key: "", name_ar: "", name_en: "", allows_decimal: false });
            toast.success(t("wm_saved"));
        });
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <Input data-testid={C.unitKey} value={draft.key} dir="ltr" placeholder={t("wm_admin_unit_key")}
                       onChange={(e) => setDraft({ ...draft, key: e.target.value })} className="h-9 w-28" />
                <Input data-testid={C.unitNameAr} value={draft.name_ar} dir="rtl" placeholder={t("wm_admin_name_ar")}
                       onChange={(e) => setDraft({ ...draft, name_ar: e.target.value })} className="h-9 w-32" />
                <Input data-testid={C.unitNameEn} value={draft.name_en} dir="ltr" placeholder={t("wm_admin_name_en")}
                       onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} className="h-9 w-32" />
                <label className="flex items-center gap-2 text-xs font-semibold text-[color:var(--ink-muted)]">
                    <Switch data-testid={C.unitDecimal} checked={draft.allows_decimal}
                            onCheckedChange={(v) => setDraft({ ...draft, allows_decimal: v })} />
                    {t("wm_admin_allows_decimal")}
                </label>
                <Button data-testid={C.unitAdd} size="sm" onClick={add} disabled={busy}
                        className="h-9 gap-1.5 text-xs font-semibold">
                    <Plus className="h-4 w-4" />
                    {t("wm_admin_unit_add")}
                </Button>
            </div>

            <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]">
                <table data-testid={C.unitsTable} className="w-full text-sm">
                    <thead className="bg-[color:var(--bg-surface-2)]">
                        <tr>
                            {["wm_admin_unit_key", "wm_admin_name_ar", "wm_admin_name_en",
                              "wm_admin_allows_decimal", "wm_admin_state"].map((h) => (
                                <th key={h} className="px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]">{t(h)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {units.map((u) => (
                            <tr key={u.key} data-testid={C.unitRow(u.key)} className="border-t border-[color:var(--separator)]">
                                <td className="px-3 py-2.5 text-xs text-[color:var(--ink-muted)]"><bdi dir="ltr">{u.key}</bdi></td>
                                <td className="px-3 py-2.5 font-semibold text-[color:var(--ink)]">{u.name_ar}</td>
                                <td className="px-3 py-2.5 text-xs text-[color:var(--ink-muted)]">{u.name_en || "—"}</td>
                                <td className="px-3 py-2.5 text-xs text-[color:var(--ink-muted)]">
                                    {t(u.allows_decimal ? "wm_admin_yes" : "wm_admin_no")}
                                </td>
                                <td className="px-3 py-2.5">
                                    <Switch data-testid={C.unitToggle(u.key)} checked={u.is_active !== false} disabled={busy}
                                            onCheckedChange={(v) => call(async () => {
                                                await api.patch(`/waste-meals/units/${u.key}`, { is_active: v });
                                            })} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
